const base64UrlToUint8Array = (value) => {
    if (!value) {
        return new Uint8Array();
    }

    const normalized = value
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

export const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
        console.warn("Notification not supported");
        return false;
    }

    if (window.Notification.permission === "granted") {
        return true;
    }

    const permission = await window.Notification.requestPermission();

    if (permission !== "granted") {
        console.warn("Permission not granted for Notification");
        return false;
    }

    return true;
};

export const showNotification = (title, options) => {
    if (!("Notification" in window)) {
        console.warn("Notification not supported");
        return null;
    }

    if (window.Notification.permission !== "granted") {
        console.warn("Permission not granted for Notification");
        return null;
    }

    return new window.Notification(title, options);
};

export const isWebPushSupported = () => {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
};

export const fetchVapidPublicKey = async () => {
    try {
        const response = await fetch('/api/notifications/config', {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to load VAPID public key: ${response.status}`);
        }

        const data = await response.json();
        return data?.webpush?.public_key || null;
    } catch (error) {
        console.error('Failed to fetch VAPID public key', error);
        return null;
    }
};

export const registerServiceWorker = async (vapidPublicKey) => {
    if (!("serviceWorker" in navigator)) {
        console.warn("Service workers are not supported in this browser");
        return null;
    }

    try {
        const workerUrl = vapidPublicKey
            ? `/service-worker.js?vapidPublicKey=${encodeURIComponent(vapidPublicKey)}`
            : '/service-worker.js';

        return await navigator.serviceWorker.register(workerUrl);
    } catch (error) {
        console.error("Failed to register the web-push service worker", error);
        return null;
    }
};

export const registerPushSubscription = async ({
    vapidPublicKey,
    registerEndpoint,
    onSuccess,
    onError,
    forceRefresh = false,
} = {}) => {
    if (!isWebPushSupported()) {
        console.warn("Web push notifications are not supported in this browser");
        return null;
    }

    const granted = await requestNotificationPermission();
    if (!granted) {
        return null;
    }

    const publicKey = vapidPublicKey ?? await fetchVapidPublicKey();
    if (!publicKey) {
        console.warn("No VAPID public key configured for push notifications");
        return null;
    }

    const registration = await registerServiceWorker(publicKey);
    if (!registration) {
        return null;
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription || forceRefresh) {
        try {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64UrlToUint8Array(publicKey),
            });
        } catch (error) {
            console.error("Failed to subscribe to push notifications", error);
            if (onError) {
                onError(error);
            }
            return null;
        }
    }

    try {
        const response = await fetch('/api/users/me/notifications/registerWebpush', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                endpoint: subscription.endpoint,
                keys: subscription.toJSON().keys,
                user_agent: navigator.userAgent,
            }),
        });

        if (!response.ok) {
            throw new Error(`Push registration failed with status ${response.status}`);
        }
    } catch (error) {
        console.error("Failed to register push subscription with backend", error);
        if (onError) {
            onError(error);
        }
        return null;
    }

    if (onSuccess) {
        onSuccess(subscription);
    }

    return subscription;
};
